import { ChangeEvent } from "react";

import { useFormikContext } from "formik";
import { useTheme } from "styled-components";

import { ColumnContainer, ColumnContainerProps, Wrapper } from "@sso/ui/FlexBox";
import { StyledSelect } from "@sso/ui/StyledSelect";
import { Typography } from "@sso/ui/Typography";

interface SelectDataType {
  label: string;
  value: number | string;
  key: number | string;
}

export interface SelectProps extends Omit<ColumnContainerProps, "maxHeight"> {
  name: string;
  label?: string;
  items: SelectDataType[];
  disabled?: boolean;
  onChange?: (value: string | number) => void;
}

const getCurrentValue = (obj: any, name: string): any => {
  if (obj == null || Object.keys(obj).length === 0) {
    return null;
  }

  if (!name.includes(".")) {
    return obj[name] ?? null;
  }

  const splitName = name.split(".");
  let value = obj;

  for (const key of splitName) {
    if (!(key in value)) {
      return null;
    }
    value = value[key];
  }

  return value == null || value === "" ? null : value;
};

const FormikSelectWithBlur = ({ name, label, items, disabled, onChange, ...containerProps }: SelectProps) => {
  const { values, handleBlur, handleChange, errors, touched } = useFormikContext();
  const currentValue = getCurrentValue(values, name) as string | number;
  const err = errors as any;
  const fieldError = getCurrentValue(err, name);
  const tch = touched as any;
  const fieldTouched = getCurrentValue(tch, name);
  const theme = useTheme();

  const onSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === "-1") {
      return;
    }
    onChange && onChange(e.target.value);
    handleChange(e);
  };
  return (
    <ColumnContainer
      position="relative"
      margin="0 0 1rem 0"
      padding="0 0.2rem"
      align="flex-start"
      justify="flex-start"
      width="100%"
      {...containerProps}
    >
      {label && !disabled && (
        <Wrapper position="absolute" left="1rem" top="-6px" padding="0 0.25rem" bgColor="white">
          <Typography size="0.75rem" textTransform="capitalize">
            {label}
          </Typography>
        </Wrapper>
      )}
      <StyledSelect
        disabled={disabled}
        placeholder="Select ..."
        onBlur={handleBlur}
        onChange={onSelect}
        value={currentValue ?? undefined}
        name={name}
      >
        {currentValue == null && (
          <option value={-1} key={-1}>
            Select ...
          </option>
        )}
        {items.map(item => (
          <option key={item.key} value={item.value}>
            {item.label}
          </option>
        ))}
      </StyledSelect>
      {fieldError && fieldTouched && (
        <Typography color={theme.colors.error.main} size="0.8rem" textAlign="left" margin="0.2rem">
          {fieldError}
        </Typography>
      )}
    </ColumnContainer>
  );
};

export default FormikSelectWithBlur;
